import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRequest, patchRequest, deleteRequest } from 'apis/ApiInstence';
import { toast } from 'react-toastify';
import { FaEdit, FaPlus, FaFilter } from 'react-icons/fa';
import './Packages.css';

const Packages = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [currentPackage, setCurrentPackage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeFilter, setActiveFilter] = useState('Residential');

  const ALL_CATEGORIES = ['Residential', 'Commercial', 'Industrial', 'Townships', 'Farmhouses'];

  useEffect(() => {
    fetchPackages();
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showModal) {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showModal]);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
      const res = await getRequest('package', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setPackages(res.data.data);
      } else {
        toast.error(res.data.message || 'Failed to load packages');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Error fetching packages');
    } finally {
      setLoading(false);
    }
  };

  const filteredPackages = useMemo(() => {
    if (activeFilter === 'All') return packages;
    return packages.filter(pkg => pkg.property_type === activeFilter);
  }, [packages, activeFilter]);

  const counts = useMemo(() => {
    const c = { All: packages.length };
    ALL_CATEGORIES.forEach(cat => {
      c[cat] = packages.filter(p => p.property_type === cat).length;
    });
    return c;
  }, [packages, ALL_CATEGORIES]);

  const openEditModal = (pkg) => {
    setCurrentPackage(JSON.parse(JSON.stringify(pkg)));
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentPackage(null);
  };

  const handlePackageChange = (field, value) => {
    setCurrentPackage((prev) => ({ ...prev, [field]: value }));
  };

  const handleCategoryChange = (catId, newName) => {
    setCurrentPackage((prev) => ({
      ...prev,
      categories: prev.categories.map((cat) =>
        cat.id === catId ? { ...cat, name: newName } : cat
      ),
    }));
  };

  const handleFeatureChange = (catId, featId, newText) => {
    setCurrentPackage((prev) => ({
      ...prev,
      categories: prev.categories.map((cat) =>
        cat.id === catId
          ? {
            ...cat,
            features: cat.features.map((feat) =>
              feat.id === featId ? { ...feat, feature: newText } : feat
            ),
          }
          : cat
      ),
    }));
  };

  const savePackageChanges = async () => {
    if (!currentPackage) return;
    setSaving(true);
    const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
    
    try {
      await patchRequest(
        `package/${currentPackage.id}`,
        { name: currentPackage.name, price_sqft: currentPackage.price_sqft, property_type: currentPackage.property_type },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      for (const cat of currentPackage.categories) {
        await patchRequest(`categories/${cat.id}`, { name: cat.name }, { headers: { Authorization: `Bearer ${token}` } });
      }

      for (const cat of currentPackage.categories) {
        for (const feat of cat.features) {
          await patchRequest(`features/${feat.id}`, { feature: feat.feature }, { headers: { Authorization: `Bearer ${token}` } });
        }
      }

      toast.success('Package updated successfully');
      closeModal();
      fetchPackages();
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const deletePackage = async (id, name) => {
    if (window.confirm(`Delete package "${name}"?`)) {
      try {
        const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
        const res = await deleteRequest(`package/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success) {
          toast.success('Package deleted successfully');
          fetchPackages();
        } else {
          toast.error(res.data.message || 'Failed to delete package');
        }
      } catch (error) {
        toast.error(error?.message || 'Failed to delete package');
      }
    }
  };

  if (loading) return <div className="text-center mt-5">Loading packages...</div>;

  return (
    <div className="container-fluid mt-4 px-4">
      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-4 bg-white p-4 rounded-lg shadow-sm">
        <div>
          <h4 className="font-weight-bold mb-1">Service Packages</h4>
          <p className="text-muted small mb-0 font-weight-normal">Manage construction packages and detailed estimates.</p>
        </div>
        <button className="btn btn-primary px-4 py-2 font-weight-bold" onClick={() => navigate('/packages/add')}>
          <FaPlus className="mr-2" /> Add Package
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="filter-wrapper mb-4 overflow-auto scrollbar-hide">
        <div className="d-flex gap-2 p-1">
          {ALL_CATEGORIES.map(cat => (
            <button 
              key={cat}
              className={`filter-tab ${activeFilter === cat ? 'active' : ''}`}
              onClick={() => setActiveFilter(cat)}
            >
              {cat} <span className="tab-count">{counts[cat]}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Grid Display */}
      <div className="row">
        {filteredPackages.length === 0 ? (
          <div className="col-12 text-center py-5">
            <div className="text-muted mb-3"><FaFilter size={40} opacity={0.2} /></div>
            <h5 className="text-muted">No packages found for {activeFilter === 'All' ? 'any category' : activeFilter}</h5>
          </div>
        ) : (
          filteredPackages.map(pkg => (
            <div key={pkg.id} className="col-md-6 col-lg-4 mb-4">
              <PackageCard
                pkg={pkg}
                onEdit={() => openEditModal(pkg)}
                onDelete={() => deletePackage(pkg.id, pkg.name)}
              />
            </div>
          ))
        )}
      </div>

      {/* Edit Modal (Accessibility-Fix applied) */}
      {showModal && currentPackage && (
        <div 
          className="modal fade show" 
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} 
          onClick={closeModal}
          onKeyDown={(e) => e.key === 'Escape' && closeModal()}
          role="button"
          tabIndex={0}
          aria-label="Close modal overlay"
        >
          <div 
            className="modal-dialog modal-dialog-centered modal-lg" 
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
            role="presentation"
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title font-weight-bold">Edit Package: {currentPackage.name}</h5>
                <button type="button" className="close" onClick={closeModal} aria-label="Close">
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 form-group">
                    <label htmlFor="p_name">Package Name</label>
                    <input id="p_name" type="text" className="form-control" value={currentPackage.name} onChange={(e) => handlePackageChange('name', e.target.value)} />
                  </div>
                  <div className="col-md-6 form-group">
                    <label htmlFor="p_price">Price (₹/sq.ft)</label>
                    <input id="p_price" type="number" className="form-control" value={currentPackage.price_sqft} onChange={(e) => handlePackageChange('price_sqft', e.target.value)} />
                  </div>
                  <div className="col-md-12 form-group">
                    <label htmlFor="p_type">Property Category</label>
                    <select id="p_type" className="form-control" value={currentPackage.property_type} onChange={(e) => handlePackageChange('property_type', e.target.value)}>
                      {ALL_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <hr />
                <h6 className="font-weight-bold mb-3">Inclusive Components</h6>
                {currentPackage.categories.map((cat) => (
                  <div key={cat.id} className="category-edit-section mb-3 p-3">
                    <div className="form-group mb-3">
                      <label htmlFor={`c_${cat.id}`}>Component Name</label>
                      <input id={`c_${cat.id}`} type="text" className="form-control font-weight-bold" value={cat.name} onChange={(e) => handleCategoryChange(cat.id, e.target.value)} />
                    </div>
                    <div className="form-group mb-0">
                      <p className="small font-weight-bold text-muted mb-2">Detailed Features</p>
                      {cat.features.map((feat) => (
                        <div key={feat.id} className="mb-2">
                          <label htmlFor={`f_${feat.id}`} className="sr-only">Feature</label>
                          <input id={`f_${feat.id}`} type="text" className="form-control form-control-sm" value={feat.feature} onChange={(e) => handleFeatureChange(cat.id, feat.id, e.target.value)} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <button className="btn btn-light" onClick={closeModal}>Cancel</button>
                <button className="btn btn-primary px-4" onClick={savePackageChanges} disabled={saving}>{saving ? 'Saving...' : 'Update Package'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PackageCard = ({ pkg, onEdit, onDelete }) => (
  <div className="package-card h-100">
    <div className="card-body p-4">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <h4 className="font-weight-bold mb-0 text-dark">{pkg.name}</h4>
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-outline-primary border-0" onClick={onEdit} title="Edit"><FaEdit /></button>
          <button className="btn btn-sm btn-outline-danger border-0" onClick={onDelete} title="Delete">Delete</button>
        </div>
      </div>
      <div className="mb-3">
        <span className="badge badge-light px-3 py-2 border rounded-pill">{pkg.property_type}</span>
      </div>
      <div className="h4 text-success font-weight-bold mb-4">₹{pkg.price_sqft} <span className="small text-muted font-weight-normal">/ sq.ft</span></div>
      <div className="package-details overflow-auto pr-2" style={{ maxHeight: '200px' }}>
        {pkg.categories.map(cat => (
          <div key={cat.id} className="mb-3">
            <h6 className="font-weight-bold text-dark border-bottom pb-2 small text-uppercase tracking-wider">{cat.name}</h6>
            <ul className="list-unstyled mb-0">
              {cat.features.map(feat => (
                <li key={feat.id} className="small text-muted mb-2 d-flex align-items-start">
                  <span className="text-primary mr-2">•</span> {feat.feature}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default Packages;
